import { useState } from "react";
import {
  useRadio,
  VisuallyHidden,
  cn,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
  Checkbox,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@nextui-org/react";
import { EllipsisVerticalIcon, TrashIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { getErrorMessage } from "../../../../../services/errorMessage";

/**
 * Kartu pilihan kendaraan dengan menu aksi di sudut kanan atas.
 *
 * Berbeda dengan CardRadioButton yang mengelompokkan per bahan B3, kartu ini
 * mengelompokkan per KENDARAAN - satu kartu mewakili seluruh baris pelaporan
 * pengangkutan kendaraan tersebut pada periode aktif.
 *
 * PROPS TAMBAHAN
 * - canManage     : tampilkan/sembunyikan menu titik tiga (mis. per role)
 * - onDelete      : async (vehicle) => void
 * - deleteWarning : kalimat konfirmasi hapus
 *
 * Tidak ada aksi edit di sini; sesuai keputusan, modul pengangkutan hanya
 * butuh hapus.
 */
export default function RadioVehicleButton({ canManage = true, onDelete, deleteWarning, noTransport = false, isSavingNoTransport = false, onToggleNoTransport, belumAdaPelaporan = false, ...props }) {
  const { Component, description, getBaseProps, getWrapperProps, getInputProps, getControlProps, getLabelWrapperProps } = useRadio(props);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Kartu ini adalah <label> milik radio, jadi klik apa pun di dalamnya akan
   * ikut memilih radio. Area menu dibungkus handler ini agar membuka menu tidak
   * sekaligus mengganti kendaraan yang dipilih.
   */
  const stopRadioActivation = {
    onClick: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onMouseDown: (e) => e.stopPropagation(),
    onKeyDown: (e) => e.stopPropagation(),
  };

  const handleConfirmDelete = async () => {
    if (typeof onDelete !== "function") {
      toast.error("Handler penghapusan data belum dipasang");
      return;
    }

    setIsDeleting(true);
    try {
      await onDelete(description);
      setIsConfirmOpen(false);
      toast.success("Laporan pengangkutan kendaraan berhasil dihapus");
    } catch (error) {
      toast.error(getErrorMessage(error, "Gagal menghapus laporan pengangkutan"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Component
        {...getBaseProps()}
        className={cn(
          "relative w-full group inline-flex items-center hover:opacity-70 active:opacity-50 justify-between flex-row-reverse tap-highlight-transparent",
          "cursor-pointer border-2 border-default rounded-lg gap-3 py-4 px-3",
          "data-[selected=true]:border-primary",
        )}
      >
        <VisuallyHidden>
          <input {...getInputProps()} />
        </VisuallyHidden>

        {/* Menu aksi di sudut kanan atas */}
        {canManage && (
          <div className="absolute top-1 right-1 z-20" {...stopRadioActivation}>
            <Dropdown placement="bottom-end">
              <DropdownTrigger>
                <Button isIconOnly size="sm" variant="light" radius="full" aria-label="Menu aksi kendaraan" className="min-w-6 w-6 h-6">
                  <EllipsisVerticalIcon className="size-4 text-default-500" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Aksi kendaraan" onAction={() => setIsConfirmOpen(true)}>
                <DropdownItem key="delete" className="text-danger" color="danger" startContent={<TrashIcon className="size-4" />}>
                  Hapus data kendaraan
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        )}

        <span {...getWrapperProps()}>
          <span {...getControlProps()} />
        </span>

        <div {...getLabelWrapperProps()}>
          <div className="w-full flex flex-col text-xs">
            <div className="flex items-center gap-2 mb-3 pr-6">
              <span className="text-sm font-semibold">{description?.modelKendaraan}</span>/<span className="text-sm">{description?.noPolisi}</span>
            </div>
            <div className="w-full flex flex-col mb-3">
              <div>
                No. Rangka: <span className="font-semibold">{description?.nomorRangka}</span>
              </div>
              <div>
                No. Mesin: <span className="font-semibold">{description?.nomorMesin}</span>
              </div>
              <div>
                Tahun Pembuatan: <span className="font-semibold">{description?.tahunPembuatan}</span>
              </div>

              {/* Kendaraan bisa saja belum punya baris pelaporan pada periode ini -
                  daftarnya diambil dari master kendaraan perusahaan. Ditandai agar
                  jelas mengapa penanda di bawahnya tidak bisa disentuh. */}
              {belumAdaPelaporan && <div className="mt-2 text-warning-600">Belum ada baris pelaporan pada periode ini</div>}

              {/* Satu baris jarak sebelum penanda, sesuai permintaan. */}
              {typeof onToggleNoTransport === "function" && (
                <div className="mt-3" {...stopRadioActivation}>
                  <Checkbox
                    size="sm"
                    isSelected={noTransport}
                    isDisabled={isSavingNoTransport || belumAdaPelaporan}
                    onValueChange={(nilai) => onToggleNoTransport(description, nilai)}
                    aria-label={`Tidak ada pengangkutan untuk ${description?.noPolisi}`}
                  >
                    <span className="text-xs">Tidak ada pengangkutan</span>
                  </Checkbox>
                </div>
              )}
            </div>
          </div>
        </div>
      </Component>

      {/* Konfirmasi hapus */}
      <Modal isOpen={isConfirmOpen} onOpenChange={setIsConfirmOpen} size="sm" isDismissable={!isDeleting}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="text-danger">Hapus Data Kendaraan</ModalHeader>
              <ModalBody>
                <p className="text-sm">
                  Hapus laporan pengangkutan{" "}
                  <span className="font-semibold">
                    {description?.modelKendaraan} ({description?.noPolisi})
                  </span>{" "}
                  {deleteWarning || "beserta seluruh detail pengangkutannya pada periode aktif?"}
                </p>
                <p className="text-xs text-default-500">Data kendaraan di master tidak terhapus. Tindakan ini tidak dapat dibatalkan.</p>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose} isDisabled={isDeleting}>
                  Batal
                </Button>
                <Button color="danger" onPress={handleConfirmDelete} isLoading={isDeleting}>
                  Hapus
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}